'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */
import React, { useCallback, useLayoutEffect, useState } from 'react';
import moment from 'moment';
import Container from '@ui/Container/Container';
import { GridColFullWidth } from '@constants/layout';
import cn from '@utils/cn';
import { StaticIcon } from '@ui/Icon/StaticIcon/StaticIcon';
import IconType from '@ui/Icon/StaticIcon/IconType';
import { isElementExistInHeader } from '@utils/validations';
import type { NotificationBarType } from '@lib/sanity/queries/notificationBar/types';
import { getCookie } from '@utils/cookie';

const COOKIE_NAME = 'notificationBarDismissed';

type NotificationBarProps = {
  data?: NotificationBarType | null;
  children: React.ReactNode;
  isDismissed?: boolean; // From server - prevents CLS
};

export default function NotificationBar({ children, data, isDismissed = false }: NotificationBarProps) {
  // Hooks must be called before any early returns
  const [isClosed, setIsClosed] = useState(false); // Start visible since server already checked

  const onClose = useCallback(() => {
    setIsClosed(true);
    if (data && data.endDate) {
      addIsDismissed(data._id, data._updatedAt);
    }
  }, [data]);

  // Double-check on client side in case cookie was set after server render
  // This is a safety check for edge cases (e.g., cookie set in another tab)
  useLayoutEffect(() => {
    if (data?.isDismissable && data?._id && data?._updatedAt) {
      const isDismissedClient = getIsDismissed(data._id, data._updatedAt);
      if (isDismissedClient) {
        setIsClosed(true);
      }
    }
  }, [data]);

  // If dismissed on server, don't render at all - prevents CLS
  if (isDismissed || !data) {
    return null;
  }

  // Check visibility conditions
  const isTranslated = isElementExistInHeader(data, ['description']);
  const isValidStartDate = !data.startDate || moment(data.startDate).isSameOrBefore(new Date(), 'day');
  const isValidEndDate = !data.endDate || moment(data.endDate).isSameOrAfter(new Date(), 'day');
  const isVisible = isTranslated && isValidStartDate && isValidEndDate;

  if (!isVisible || isClosed) {
    return null;
  }
  return (
    <Container id="notification-bar" theme={data.theme} paddingType="none" className="bg-background">
      <div className={cn('flex space-x-2 px-4 py-3', GridColFullWidth)}>
        <div className="flex grow items-center justify-center ">{children}</div>
        {data?.isDismissable && (
          <div className="flex h-full shrink-0 items-start justify-center">
            <StaticIcon
              icon={IconType.closeSmall}
              className={cn('h-8 w-8 cursor-pointer', 'fill-muted-foreground hover:fill-foreground transition')}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    </Container>
  );
}

function getIsDismissed(id: string, updatedAt: string) {
  const idString = getIdString(id, updatedAt);
  const cookieValue = getCookie(COOKIE_NAME);
  return cookieValue?.split(',').includes(idString) ?? false;
}

function addIsDismissed(id: string, updatedAt: string) {
  const idString = getIdString(id, updatedAt);
  const cookieValue = getCookie(COOKIE_NAME);
  const dismissed = cookieValue?.split(',') ?? [];
  if (!dismissed.includes(idString)) {
    dismissed.unshift(idString);
  }
  const newValue = dismissed.slice(0, 5).join(',');
  // Set cookie with 1 year expiration
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);
  document.cookie = `${COOKIE_NAME}=${newValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
}

function getIdString(id: string, updatedAt: string) {
  return `${id}-${updatedAt}`;
}
